import { FormProvider } from "react-hook-form";
import {
  AlertDialog,
  AlertDialogTrigger,
} from "@/view/components/ui/alert-dialog";
import { Card } from "@/view/components/ui/card";
import { Spinner } from "@/view/components/ui/spinner";
import ButtonBack from "@/view/components/ButtonBack";
import { useAuth } from "@/app/hooks/useAuth";
import { PartnerType, getPartnerLabel } from "@/app/utils/partners.enum";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { TriStateEnum } from "@/app/entities/TriState";
import { usePartnersPageController } from "./usePartnersPageController";
import { EditPartner } from "./components/EditPartner";
import { TablePage } from "../TablePage";
import { AddTableCard } from "../TablePage/components/AddTableCard";
import { Settings } from "lucide-react";
import { ConfigPartner } from "./components/ConfigPartner";
import { withCdn } from "@/app/utils/withCdn";

export default function PartnersPage() {
  const {
    productsConfig,
    isLoading,
    PERMISSIONS,
    selectedPartner,
    setSelectedPartner,
    selectedPartnerData,
    setSelectedPartnerData,
    methods,
    partnersConfig,
    isOpenConfigPartner,
    setIsOpenConfigPartner,
    externalProducts,
    selectedConfigProduct,
    setSelectedConfigProduct,
  } = usePartnersPageController();

  const { isSuperAdmin } = useAuth();

  if (isLoading) {
    return <Spinner />;
  }

  if (
    selectedPartner === PartnerType.FGTS ||
    selectedPartner === PartnerType.CLT
  ) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex w-full items-center justify-between">
          <ButtonBack
            label={`Produtos - ${getPartnerLabel(selectedPartner as PartnerType)}`}
            action={() => setSelectedPartner(null)}
          />
          {PERMISSIONS.tables.create && (
            <FormProvider {...methods}>
              <AddTableCard type={selectedPartner} />
            </FormProvider>
          )}
        </div>

        <TablePage type={selectedPartner} />
      </div>
    );
  }

  const getPartnerData = (partner: PartnerType) => {
    const productImage = productsConfig?.products[partner]?.image;
    return {
      image:
        (productImage && withCdn(productImage)) ||
        externalProducts?.[partner]?.image ||
        getPartnerImage(partner),
      color:
        productsConfig?.products[partner]?.color ||
        externalProducts?.[partner]?.color ||
        getPartnerBgColor(partner),
      label:
        productsConfig?.products[partner]?.label ||
        externalProducts?.[partner]?.title ||
        getPartnerLabel(partner),
    };
  };

  return (
    <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
      {Object.entries(productsConfig?.products || {})
        .filter(([_, productDetail]) => {
          if (isSuperAdmin) return true;
          return (
            productDetail.state === TriStateEnum.ON ||
            productDetail.state === TriStateEnum.FULL
          );
        })
        .map(([partnerKey, productDetail]) => {
          const partner = partnerKey as PartnerType;
          const config = partnersConfig[partner];

          if (!config) return null;
          const hasTablePage =
            partner === PartnerType.FGTS || partner === PartnerType.CLT;
          const itemsToShow = hasTablePage
            ? productDetail.itens.slice(0, 3)
            : config.itemsToShow(productDetail.itens);

          return (
            <AlertDialog key={partner}>
              <AlertDialogTrigger asChild>
                <Card
                  className="cursor-pointer p-6 transition-shadow hover:shadow-lg"
                  onClick={() => {
                    if (hasTablePage) {
                      setSelectedPartner(partner);
                    } else {
                      setSelectedPartnerData({
                        partner,
                        itens: productDetail.itens,
                        tableId: productDetail.tableId || productDetail._id,
                        delay: productDetail.delay,
                        isActive: productDetail.isActive,
                        state: productDetail.state,
                      });
                    }
                  }}
                >
                  <div
                    className="flex h-[110px] w-full items-center justify-center overflow-hidden rounded-lg"
                    style={{ backgroundColor: getPartnerData(partner).color }}
                  >
                    <img
                      alt="parceiro img"
                      src={getPartnerData(partner).image}
                      className="h-full w-auto object-contain"
                      style={{ aspectRatio: "2/1" }}
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <p className="font-bold">{getPartnerData(partner).label}</p>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedConfigProduct(productDetail);
                        setIsOpenConfigPartner(true);
                      }}
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm font-medium text-black03">
                      {config.title}
                    </p>
                    <p className="font-medium text-black03">
                      {productDetail.itens.length}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-col gap-1">
                    {itemsToShow.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <p className="text-sm font-medium text-black04">
                          {config.renderLabel(item, index, itemsToShow)}
                        </p>
                        <div className="flex-1 border-t border-dashed border-black04"></div>
                        <p className="text-sm font-medium">
                          {config.renderValue(item)}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </AlertDialogTrigger>

              <EditPartner partnerData={selectedPartnerData} />
            </AlertDialog>
          );
        })}

      <ConfigPartner
        open={isOpenConfigPartner}
        onOpenChange={setIsOpenConfigPartner}
        product={selectedConfigProduct}
      />
    </div>
  );
}
